import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";
import { logger } from "../../utils/logger";

/**
 * API hook for texture generation operations
 * Used for generating seamless 2D textures from plank/tile images
 * Applies to Surface family products (tiles, hardwood, wallpaper, etc.)
 */
export const useTextureApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    /**
     * Generate a seamless texture from plank images for a specific pattern
     * @param body - Parameters including files, dimensions, and pattern settings
     * @param pattern - The pattern type (e.g., 'bricks', 'herringbone')
     * @returns Base64 encoded texture image
     */
    const generateTextureForPattern = async (body: any, pattern: string): Promise<{ image: string, textureWidth?: number, textureHeight?: number }> => {
        logger.debug(`[DEBUG API] Starting generateTextureForPattern for pattern ${pattern}`);
        logger.debug(`[DEBUG API] Body contains ${body.files?.length || 0} files`);

        let data = new FormData();
        if (body.plankWidth)
            data.append('plankWidth', body.plankWidth);
        if (body.plankHeight)
            data.append('plankHeight', body.plankHeight);
        if (body.textureWidth)
            data.append('textureWidth', body.textureWidth);
        if (body.textureHeight)
            data.append('textureHeight', body.textureHeight);
        if (body.pattern)
            data.append('pattern', body.pattern);
        if (body.octoImageHeight)
            data.append('octoImageHeight', body.octoImageHeight);
        if (body.squareImageHeight)
            data.append('squareImageHeight', body.squareImageHeight);
        if (body.groutLineWidth)
            data.append('groutLineWidth', body.groutLineWidth);
        if (body.groutLineColor)
            data.append('groutLineColor', body.groutLineColor);
        if (body.lengthL1)
            data.append('lengthL1', body.lengthL1);
        if (body.lengthL2)
            data.append('lengthL2', body.lengthL2);
        if (body.lengthL3)
            data.append('lengthL3', body.lengthL3);
        if (body.offset)
            data.append('offset', body.offset);
        if (body.randomize !== undefined)
            data.append('randomize', String(body.randomize));
        if (body.mirror !== undefined)
            data.append('mirror', String(body.mirror));

        logger.debug(`[DEBUG API] FormData initialized with parameters`);

        let filePromises: Promise<boolean>[] = [];
        let octoPromises: Promise<boolean>[] = [];
        let squarePromises: Promise<boolean>[] = [];

        if (body.files && body.files.length > 0) {
            logger.debug(`[DEBUG API] Processing ${body.files.length} files for pattern ${pattern}`);
            filePromises = body.files.map((file: File, index: number) => {
                return new Promise<boolean>((resolve, reject) => {
                    try {
                        logger.debug(`[DEBUG API] Processing file ${index}:`, file.name);
                        const reader = new FileReader();
                        reader.readAsArrayBuffer(file);
                        reader.onload = () => {
                            try {
                                const blob = new Blob([reader.result as ArrayBuffer], { type: file.type });
                                data.append("files", blob, "file" + index + ".jpg");
                                logger.debug(`[DEBUG API] Added file ${index} to FormData`);
                                resolve(true);
                            } catch (blobError) {
                                logger.error(`[ERROR API] Failed to create blob for file ${index}:`, blobError);
                                reject(blobError);
                            }
                        };
                        reader.onerror = (error) => {
                            logger.error(`[ERROR API] FileReader error for file ${index}:`, error);
                            reject(error);
                        };
                    } catch (fileError) {
                        logger.error(`[ERROR API] Exception handling file ${index}:`, fileError);
                        reject(fileError);
                    }
                });
            });
        } else {
            logger.error(`[ERROR API] No files provided for pattern ${pattern}`);
        }

        if (body.imageA) {
            filePromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(body.imageA);
                reader.onload = () => {
                    const blob = new Blob([reader.result as ArrayBuffer], { type: body.imageA.type });
                    data.append("imageA", blob, body.imageA.name);
                    resolve(true);
                };
                reader.onerror = reject;
            }));
        }

        if (body.imageB) {
            filePromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(body.imageB);
                reader.onload = () => {
                    const blob = new Blob([reader.result as ArrayBuffer], { type: body.imageB.type });
                    data.append("imageB", blob, body.imageB.name);
                    resolve(true);
                };
                reader.onerror = reject;
            }));
        }

        if (body.imageC) {
            filePromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(body.imageC);
                reader.onload = () => {
                    const blob = new Blob([reader.result as ArrayBuffer], { type: body.imageC.type });
                    data.append("imageC", blob, body.imageC.name);
                    resolve(true);
                };
                reader.onerror = reject;
            }));
        }

        if (body.imageD) {
            filePromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsArrayBuffer(body.imageD);
                reader.onload = () => {
                    const blob = new Blob([reader.result as ArrayBuffer], { type: body.imageD.type });
                    data.append("imageD", blob, body.imageD.name);
                    resolve(true);
                };
                reader.onerror = reject;
            }));
        }

        if (body.octoImages) {
            octoPromises = body.octoImages.map((file: File, index: number) => {
                return new Promise<boolean>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(file);
                    reader.onload = () => {
                        const blob = new Blob([reader.result as ArrayBuffer], { type: file.type });
                        data.append("octoImages", blob, "file_ocot_" + index + ".jpg");
                        resolve(true);
                    };
                    reader.onerror = reject;
                });
            });
        }

        if (body.squareImages) {
            squarePromises = body.squareImages.map((file: File, index: number) => {
                return new Promise<boolean>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(file);
                    reader.onload = () => {
                        const blob = new Blob([reader.result as ArrayBuffer], { type: file.type });
                        data.append("squareImages", blob, "file_square_" + index + ".jpg");
                        resolve(true);
                    };
                    reader.onerror = reject;
                });
            });
        }

        try {
            logger.debug(`[DEBUG API] Waiting for all file promises to resolve`);
            await Promise.all(filePromises);
            await Promise.all(octoPromises);
            await Promise.all(squarePromises);

            logger.debug(`[DEBUG API] All files processed, submitting job for pattern ${pattern}`);

            // Submit the job and get jobId
            const requestUrl = routes.texture.generateForPattern(pattern);
            logger.debug(`[DEBUG API] Request URL: ${requestUrl}`);

            const jobResponse: any = await sendAuthGuardedRequest(
                ApiMethod.POST,
                requestUrl,
                data,
                {
                    //@ts-ignore
                    maxBodyLength: Infinity,
                    timeout: 2 * 60 * 1000,
                }
            );

            logger.debug(`[DEBUG API] Job submitted for pattern ${pattern}, jobId: ${jobResponse.jobId}`);

            // Poll for job completion
            const result = await pollTextureJobStatus(jobResponse.jobId);

            logger.debug(`[DEBUG API] Job completed for pattern ${pattern}`);
            return result;
        } catch (error) {
            logger.error(`[ERROR API] Error in generateTextureForPattern for pattern ${pattern}:`, error);
            throw error;
        }
    };

    /**
     * Poll for texture generation job completion
     * @param jobId - The job ID to poll
     * @param maxAttempts - Maximum polling attempts (default: 60)
     * @returns Base64 encoded texture image
     */
    const pollTextureJobStatus = async (jobId: string, maxAttempts: number = 60): Promise<{ image: string, textureWidth?: number, textureHeight?: number }> => {
        logger.debug(`[DEBUG API] Starting to poll job status for jobId: ${jobId}`);

        let attempts = 0;
        const pollInterval = 2000; // 2 seconds

        while (attempts < maxAttempts) {
            try {
                const statusResponse: any = await sendAuthGuardedRequest(
                    ApiMethod.GET,
                    routes.texture.jobStatus(jobId)
                );

                logger.debug(`[DEBUG API] Job status for ${jobId}: ${statusResponse.status}, attempt ${attempts + 1}`);

                if (statusResponse.status === 'completed') {
                    logger.debug(`[DEBUG API] Job ${jobId} completed, downloading result from: ${statusResponse.resultUrl}`);

                    // Download the result image from the resultUrl
                    const imageResponse = await fetch(statusResponse.resultUrl);
                    const arrayBuffer = await imageResponse.arrayBuffer();

                    // Convert to base64
                    const uint8Array = new Uint8Array(arrayBuffer);
                    let base64String = '';
                    const chunkSize = 0x8000; // 32 KB chunks
                    for (let i = 0; i < uint8Array.length; i += chunkSize) {
                        base64String += String.fromCharCode.apply(null, Array.from(uint8Array.subarray(i, i + chunkSize)));
                    }

                    const result = btoa(base64String);
                    logger.debug(`[DEBUG API] Successfully downloaded and converted result for job ${jobId}, dimensions: ${statusResponse.textureWidth}x${statusResponse.textureHeight}m`);
                    return {
                        image: result,
                        textureWidth: statusResponse.textureWidth,
                        textureHeight: statusResponse.textureHeight,
                    };
                } else if (statusResponse.status === 'failed') {
                    logger.error(`[ERROR API] Job ${jobId} failed: ${statusResponse.error}`);
                    throw new Error(`Texture generation failed: ${statusResponse.error}`);
                }

                // Still pending or processing, wait before next attempt
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                attempts++;
            } catch (error) {
                logger.error(`[ERROR API] Error polling job status for ${jobId}:`, error);
                throw error;
            }
        }

        throw new Error(`Job ${jobId} timed out after ${maxAttempts * pollInterval / 1000} seconds`);
    };

    /**
     * Get the status of a texture generation job
     * @param jobId - The job ID to check
     */
    const getTextureJobStatus = async (jobId: string): Promise<any> => {
        logger.debug(`[DEBUG API] Getting status for job ${jobId}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.texture.jobStatus(jobId)
        );
        return response;
    };

    /**
     * Get list of texture generation jobs
     * @param status - Filter by status
     * @param limit - Maximum number of jobs to return
     */
    const getTextureJobs = async (status?: string, limit?: number): Promise<any> => {
        logger.debug(`[DEBUG API] Getting texture jobs, status: ${status}, limit: ${limit}`);
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (limit) params.append('limit', limit.toString());
        const queryString = params.toString() ? `?${params.toString()}` : '';

        const response = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.texture.jobs + queryString
        );
        return response;
    };

    /**
     * Retry a failed texture generation job
     * @param jobId - The job ID to retry
     */
    const retryTextureJob = async (jobId: string): Promise<any> => {
        logger.debug(`[DEBUG API] Retrying job ${jobId}`);
        const response = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.texture.retry(jobId)
        );
        return response;
    };

    return {
        generateTextureForPattern,
        pollTextureJobStatus,
        getTextureJobStatus,
        getTextureJobs,
        retryTextureJob,
    };
};
